import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import soundEffects from '../utils/soundEffects';
import Icon from './Icon';
import logo from '../assets/logo-typing-matrix.png';
import '../static/css/Header.css';

// Optimized logo for better Lighthouse performance
const LOGO_SRC = process.env.PUBLIC_URL + '/logo-typing-matrix-40x40.webp';

const navItems = [
  { to: '/', label: 'Home', exact: true },
  { to: '/practice', label: 'Practice' },
  { to: '/lessons', label: 'Lessons' },
  { to: '/bugmode', label: 'Bug Mode' },
  { to: '/tools/wpm-calculator', label: 'WPM Calculator' },
  { to: '/blog', label: 'Blog' },
  { to: '/features', label: 'Features' },
  { to: '/about', label: 'About' },
];

const Header = () => {
  const [soundEnabled, setSoundEnabled] = useState(soundEffects.enabled);
  const [clickEnabled, setClickEnabled] = useState(soundEffects.clickEnabled);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastScroll = useRef(0);
  const location = useLocation();

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  // Close menu/settings on route change
  useEffect(() => {
    setMenuOpen(false);
    setSettingsOpen(false);
  }, [location.pathname]);

  // Close settings when clicking outside
  useEffect(() => {
    if (!settingsOpen) return;
    const onDocumentClick = (e) => {
      if (!e.target.closest('.header-settings')) setSettingsOpen(false);
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [settingsOpen]);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    const container = document.querySelector('.main-content');
    if (!container) return;

    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const current = container.scrollTop;
          const isHidden = current > lastScroll.current && current > 60;
          const isScrolled = current > 20;

          setHidden((prev) => (prev !== isHidden ? isHidden : prev));
          setScrolled((prev) => (prev !== isScrolled ? isScrolled : prev));
          lastScroll.current = current;
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(soundEffects.toggle());
  }, []);

  const toggleClickSound = useCallback(() => {
    setClickEnabled(soundEffects.toggleClick());
  }, []);

  const mobileNavRef = useRef(null);

  // Trap focus inside mobile nav when open
  useEffect(() => {
    if (!menuOpen || !mobileNavRef.current) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }

      // Focus trap
      if (e.key === 'Tab') {
        const focusableElements = mobileNavRef.current.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const navLinkClassName = useCallback(
    ({ isActive }) => `header-nav-link${isActive ? ' header-nav-link--active' : ''}`,
    []
  );

  return (
    <>
      <header
        className={`header${hidden ? ' header--hidden' : ''}${scrolled ? ' header--scrolled' : ''}`}
        role="banner"
      >
        <div className="header-bar">
          {/* Brand */}
          <div className="header-left">
            <Link to="/" className="header-brand" aria-label="The Typing Matrix – home">
              <picture>
                <source srcSet={LOGO_SRC} type="image/webp" />
                <img
                  src={logo}
                  alt=""
                  aria-hidden="true"
                  className="header-logo"
                  width="22"
                  height="22"
                  loading="eager"
                />
              </picture>
              <span className="header-brand-text">TYPING MATRIX</span>
            </Link>
            <span className="header-subtitle" aria-hidden="true">// Master Your Coding Speed</span>
          </div>

          {/* Desktop nav */}
          <nav
            id="main-navigation"
            className="header-nav"
            role="navigation"
            aria-label="Main navigation"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={navLinkClassName}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right controls */}
          <div className="header-right">
            <div className="header-settings">
              <button
                type="button"
                className="header-icon-btn"
                onClick={() => setSettingsOpen((prev) => !prev)}
                aria-expanded={settingsOpen}
                aria-label="Sound settings"
                title="Sound settings"
              >
                <Icon name="settings" size={13} />
              </button>
              {settingsOpen && (
                <div className="header-settings-menu" role="menu" aria-label="Sound options">
                  <div className="header-settings-title">SOUND OPTIONS</div>
                  <button
                    type="button"
                    className="header-settings-item"
                    onClick={toggleSound}
                    role="menuitemcheckbox"
                    aria-checked={soundEnabled}
                  >
                    <span className="settings-item-label">Typing Sound</span>
                    <span className={`settings-item-state ${soundEnabled ? 'on' : 'off'}`}>
                      {soundEnabled ? 'ON' : 'OFF'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="header-settings-item"
                    onClick={toggleClickSound}
                    role="menuitemcheckbox"
                    aria-checked={clickEnabled}
                  >
                    <span className="settings-item-label">Click Sound</span>
                    <span className={`settings-item-state ${clickEnabled ? 'on' : 'off'}`}>
                      {clickEnabled ? 'ON' : 'OFF'}
                    </span>
                  </button>
                </div>
              )}
            </div>

            <Link to="/practice" className="cyber-button header-cta" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', textDecoration: 'none' }}>
              Start Typing
            </Link>

            <div className="header-status" aria-hidden="true">
              <span className="header-status-dot"></span>
            </div>

            {/* Hamburger — mobile only */}
            <button
              type="button"
              className="header-menu-btn"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <span className={`hamburger${menuOpen ? ' hamburger--open' : ''}`} aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer overlay */}
      {menuOpen && (
        <div
          className="mobile-overlay"
          aria-hidden="true"
          onClick={closeMenu}
        />
      )}

      <nav
        ref={mobileNavRef}
        id="mobile-navigation"
        className={`mobile-nav${menuOpen ? ' mobile-nav--open' : ''}`}
        role="navigation"
        aria-label="Mobile navigation"
        aria-hidden={!menuOpen}
        inert={!menuOpen ? '' : undefined}
      >
        <div className="mobile-nav-header">
          <span className="mobile-nav-title">NAVIGATION</span>
          <button
            type="button"
            className="mobile-nav-close"
            onClick={closeMenu}
            aria-label="Close navigation menu"
          >
            ✕
          </button>
        </div>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            className={navLinkClassName}
            onClick={closeMenu}
            tabIndex={menuOpen ? 0 : -1}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
};

export default React.memo(Header);
